import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import Navbar from './Navbar';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiMonitor, FiArrowRight } = FiIcons;

const PricingPage = () => {
  const { user, updatePlan } = useAuth();

  const plans = [
    {
      name: 'Free',
      price: '$0',
      period: '/month',
      description: 'Perfect for getting started',
      features: [
        '2 monitors',
        '1GB storage',
        'Basic templates',
        'Email support',
        'Standard resolution'
      ],
      popular: false,
      current: user?.plan === 'free'
    },
    {
      name: 'Professional',
      price: '$29',
      period: '/month',
      description: 'Best for small businesses',
      features: [
        '10 monitors',
        '10GB storage',
        'Premium templates',
        'Priority support',
        'HD resolution',
        'Analytics dashboard',
        'Custom branding'
      ],
      popular: true,
      current: user?.plan === 'professional'
    },
    {
      name: 'Enterprise',
      price: '$99',
      period: '/month',
      description: 'For large organizations',
      features: [
        'Unlimited monitors',
        '100GB storage',
        'Custom templates',
        '24/7 phone support',
        '4K resolution',
        'Advanced analytics',
        'White-label solution',
        'API access',
        'Dedicated account manager'
      ],
      popular: false,
      current: user?.plan === 'enterprise'
    }
  ];

  const handlePlanSelect = (planName) => {
    if (user) {
      updatePlan(planName.toLowerCase());
      toast.success(`Upgraded to ${planName} plan!`);
    } else {
      toast.error('Please sign in to upgrade your plan');
    }
  };

  return (
    <div className="min-h-screen">
      {user ? <Navbar /> : (
        <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-white/10 border-b border-white/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiMonitor} className="text-white text-lg" />
                </div>
                <span className="text-white font-bold text-xl">SignageCloud</span>
              </Link>
              <Link 
                to="/auth" 
                className="px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg text-white hover:bg-white/30 transition-all duration-300"
              >
                Sign In
              </Link>
            </div>
          </div>
        </nav>
      )}
      
      <div className={`${user ? 'pt-20' : 'pt-32'} px-4 pb-16`}>
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Choose Your Plan
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Scale your digital signage network with plans designed for every business size
            </p>
          </motion.div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative bg-white/10 backdrop-blur-lg border rounded-2xl p-8 ${
                  plan.popular 
                    ? 'border-purple-400 ring-2 ring-purple-400/20' 
                    : 'border-white/20'
                } ${plan.current ? 'ring-2 ring-green-400/20 border-green-400' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}

                {plan.current && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-white/70 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center">
                    <span className="text-4xl font-bold text-white">{plan.price}</span>
                    <span className="text-white/70 ml-1">{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <div className="w-5 h-5 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center flex-shrink-0">
                        <SafeIcon icon={FiCheck} className="text-white text-xs" />
                      </div>
                      <span className="text-white/80">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handlePlanSelect(plan.name)}
                  disabled={plan.current}
                  className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${
                    plan.current
                      ? 'bg-green-500/20 border border-green-400/30 text-green-400 cursor-not-allowed'
                      : plan.popular
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600'
                      : 'bg-white/10 border border-white/20 text-white hover:bg-white/20'
                  }`}
                >
                  {plan.current ? 'Current Plan' : 'Get Started'}
                  {!plan.current && <SafeIcon icon={FiArrowRight} className="ml-2" />}
                </button>
              </motion.div>
            ))}
          </div>

          {/* FAQ Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-20 text-center"
          >
            <h2 className="text-3xl font-bold text-white mb-8">
              Frequently Asked Questions
            </h2>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Can I change my plan anytime?
                </h3>
                <p className="text-white/70">
                  Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Is there a free trial?
                </h3>
                <p className="text-white/70">
                  Yes, all plans come with a 14-day free trial. No credit card required for the free plan.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  What payment methods do you accept?
                </h3>
                <p className="text-white/70">
                  We accept all major credit cards, PayPal, and bank transfers for enterprise customers.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Do you offer custom solutions?
                </h3>
                <p className="text-white/70">
                  Yes, we offer custom enterprise solutions. Contact our sales team for more information.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;